import { Box, Typography } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CRM_PRO_UPDATE_TASK_PROGRESS } from "../../constants";
import { useHasPermission } from "../../utils";
import { ActionButton, FormDialog, InputField } from "../v3";
import { LeadService } from "../../services/lead.service";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const UpdateTaskProgress = ({ open, task, handleCloseModal, onSuccess }) => {
  const { user } = useSelector((state: any) => state.user);
  const hasUpdateTaskProgress = useHasPermission(CRM_PRO_UPDATE_TASK_PROGRESS);

  const [status, setStatus] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setStatus(task?.status || "TODO");
    setNotes("");
    setSelectedFile(null);
  }, [open, task?.id, task?.status]);

  const canUpdate = hasUpdateTaskProgress && user && task && (user.id === task.assignee?.id || user.id === task.createdById);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size must be below 5MB");
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    if (!task || !canUpdate) return;

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("status", status);
      if (notes) formData.append("notes", notes);
      if (selectedFile) formData.append("attachment", selectedFile);

      await LeadService.addTaskProgress(task.id, formData);

      toast.success("Task progress updated successfully");
      onSuccess();
      handleCloseModal();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update task progress");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={handleCloseModal}
      onSubmit={handleSave}
      title="Update Task Progress"
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" disabled={isSaving} />
          <ActionButton buttonText="Save" type="submit" disabled={isSaving || !canUpdate} loading={isSaving} />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={2} sx={{ py: 1 }}>
        <InputField
          label="Status"
          name="status"
          select
          selectOptions={[
            { label: "To Do", value: "TODO" },
            { label: "In Progress", value: "INPROGRESS" },
            { label: "Done", value: "DONE" },
          ]}
          value={status}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatus(e.target.value)}
          fullWidth
          disabled={isSaving}
        />

        <InputField
          label="Notes"
          name="notes"
          placeholder="Add notes about the progress"
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
          multiline={true}
          minRows={6}
          fullWidth={true}
          disabled={isSaving}
        />

        {/* File attachment */}
        <Box
          sx={{
            border: "2px dashed #e0e0e0",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: isSaving ? "default" : "pointer",
            backgroundColor: "#fafafa",
            opacity: isSaving ? 0.6 : 1,
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: isSaving ? "#fafafa" : "#f5f5f5",
              borderColor: isSaving ? "#e0e0e0" : "#674BF5",
            },
          }}
          onClick={() => !isSaving && document.getElementById("task-progress-file-upload")?.click()}
        >
          <input
            id="task-progress-file-upload"
            type="file"
            hidden
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileChange}
            disabled={isSaving}
          />
          <CloudUploadOutlined sx={{ fontSize: 40, color: "#9e9e9e", mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {selectedFile ? selectedFile.name : "Click to attach document"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Optional • PDF, DOC, DOCX, Images (Max 5MB)
          </Typography>
        </Box>
      </Box>
    </FormDialog>
  );
};
